import { TaskStatus, type TaskResponse } from "api/model";

/** What a per-plot task reports in its results summary. */
interface PlotTaskSummary {
  failed: number;
  ortho_id: number;
  succeeded: number;
  trial_id: number;
}

/**
 * Helpers for the per-plot tasks (analyze_emergence, analyze_canopy_cover), which run on
 * one ortho for one trial and report an `{ ortho_id, trial_id }` summary.
 */

const getPlotTaskSummary = (
  task: TaskResponse,
): Partial<PlotTaskSummary> | null =>
  (task.results?.summary as PlotTaskSummary | null | undefined) ?? null;

/** Null until the task has produced a summary. */
export const getPlotTaskOrthoId = (task: TaskResponse): number | null =>
  getPlotTaskSummary(task)?.ortho_id ?? null;

/** Null until the task has produced a summary. */
export const getPlotTaskTrialId = (task: TaskResponse): number | null =>
  getPlotTaskSummary(task)?.trial_id ?? null;

const hasTaskErrors = (task: TaskResponse): boolean => {
  const errorCount = task.results?.errors?.length ?? 0;
  if (errorCount > 0) {
    return true;
  }
  return (getPlotTaskSummary(task)?.failed ?? 0) > 0;
};

/** Failed outright, or completed with some plots failing. */
export const isRetryableTask = (task: TaskResponse | undefined): boolean =>
  task != null &&
  (task.status === TaskStatus.failed ||
    (task.status === TaskStatus.completed && hasTaskErrors(task)));

export const getRetryableTaskMessage = (
  task: TaskResponse | undefined,
  taskLabel: string,
): string | null => {
  if (task == null || !isRetryableTask(task)) {
    return null;
  }

  const errorMessages = (task.results?.errors ?? []).flatMap((error) => {
    const message = error.message?.trim();
    return message != null && message !== "" ? [message] : [];
  });
  const failedCount = getPlotTaskSummary(task)?.failed ?? 0;

  if (task.status === TaskStatus.failed) {
    return errorMessages.length > 0
      ? `${taskLabel} failed: ${errorMessages.join(" ")}`
      : `${taskLabel} failed.`;
  }

  if (errorMessages.length > 0) {
    return `${taskLabel} completed with errors: ${errorMessages.join(" ")}`;
  }
  if (failedCount > 0) {
    const plotLabel = failedCount === 1 ? "plot" : "plots";
    return `${taskLabel} completed with ${failedCount} failed ${plotLabel}.`;
  }
  return `${taskLabel} completed with errors.`;
};
